import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import DashboardLayout from "./DashboardLayout";
import { API_BASE } from "../utils/apiBase";

const TYPE_OPTIONS = [
  { value: "multiple_choice", label: "Multiple Choice" },
  { value: "single_choice", label: "Single Choice" },
  { value: "true_false", label: "True / False" },
  { value: "image_multiple_choice", label: "Image + Multiple Choice" },
  { value: "image_single_choice", label: "Image + Single Choice" },
  { value: "image_true_false", label: "Image + True / False" },
];

const blankQ = {
  question_type: "multiple_choice",
  question: "",
  options: ["", "", "", ""],
  correct_answers: ["A"],
  image_file: null,
  image_preview: "",
};

function NewQuestionModulePage() {
  const [meta, setMeta] = useState({ academy_categories: [], models: [], categories: [], courses: [] });
  const [form, setForm] = useState({
    set_name: "",
    academy_category: "",
    course_model_id: "",
    course_category_id: "",
    course_id: "",
  });
  const [questions, setQuestions] = useState([{ ...blankQ }]);
  const [saving, setSaving] = useState(false);
  const [entryMode, setEntryMode] = useState("manual");
  const [csvSets, setCsvSets] = useState([]);
  const [selectedCsvSetId, setSelectedCsvSetId] = useState("");

  useEffect(() => {
    axios.get(`${API_BASE}/question-modules/meta`).then((res) => setMeta(res.data || {})).catch(() => {});
    axios.get(`${API_BASE}/question-modules?source=csv`).then((res) => setCsvSets(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  }, []);

  const models = useMemo(() => (meta.models || []).filter((m) => !form.academy_category || m.academy_category === form.academy_category), [meta.models, form.academy_category]);
  const categories = useMemo(() => (meta.categories || []).filter((c) => !form.academy_category || c.academy_category === form.academy_category), [meta.categories, form.academy_category]);
  const courses = useMemo(() => (meta.courses || []).filter((c) => (!form.academy_category || c.academy_category === form.academy_category) && (!form.course_model_id || String(c.course_model_id) === String(form.course_model_id)) && (!form.course_category_id || String(c.course_category_id) === String(form.course_category_id))), [meta.courses, form.academy_category, form.course_model_id, form.course_category_id]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setQuestion = (idx, key, value) => setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, [key]: value } : q)));
  const setOption = (idx, optIdx, value) =>
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== idx) return q;
        const options = [...q.options];
        options[optIdx] = value;
        return { ...q, options };
      })
    );

  const handleTypeChange = (idx, type) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== idx) return q;
        if (type === "true_false" || type === "image_true_false") {
          return { ...q, question_type: type, options: ["True", "False"], correct_answers: ["A"] };
        }
        return { ...q, question_type: type, options: ["", "", "", ""], correct_answers: ["A"] };
      })
    );
  };

  const toggleMultiCorrect = (idx, key) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== idx) return q;
        const exists = q.correct_answers.includes(key);
        const next = exists ? q.correct_answers.filter((x) => x !== key) : [...q.correct_answers, key];
        return { ...q, correct_answers: next.length ? next : ["A"] };
      })
    );
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("set_name", form.set_name);
      fd.append("academy_category", form.academy_category);
      fd.append("course_model_id", String(form.course_model_id));
      fd.append("course_category_id", String(form.course_category_id));
      fd.append("course_id", String(form.course_id));

      const payloadQuestions = questions.map((q) => ({
        question_type: q.question_type,
        question: q.question,
        options: q.question_type.includes("true_false") ? ["True", "False"] : q.options,
        correct_answers: q.correct_answers,
      }));
      fd.append("questions", JSON.stringify(payloadQuestions));

      questions.forEach((q, i) => {
        if (q.image_file) {
          fd.append(`question_image_${i + 1}`, q.image_file);
        }
      });

      await axios.post(`${API_BASE}/question-modules`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      Swal.fire("Success", "Question module created.", "success");
      setQuestions([{ ...blankQ }]);
    } catch (err) {
      Swal.fire("Failed", err?.response?.data?.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const loadFromCsvSet = async () => {
    if (!selectedCsvSetId) return;
    const res = await axios.get(`${API_BASE}/question-modules/${selectedCsvSetId}`);
    const module = res.data?.module || null;
    const items = Array.isArray(res.data?.questions) ? res.data.questions : [];
    if (module) {
      setForm((p) => ({
        ...p,
        set_name: `${module.set_name}-copy`,
        academy_category: module.academy_category || "",
        course_model_id: module.course_model_id ? String(module.course_model_id) : "",
        course_category_id: module.course_category_id ? String(module.course_category_id) : "",
        course_id: module.course_id ? String(module.course_id) : "",
      }));
    }
    setQuestions(items.length ? items.map((q) => ({
      question_type: q.question_type || "multiple_choice",
      question: q.question || "",
      options: [q.option_a || "", q.option_b || "", q.option_c || "", q.option_d || ""],
      correct_answers: q.correct_answers_json ? (() => { try { const x = JSON.parse(q.correct_answers_json); return Array.isArray(x) && x.length ? x : [q.correct_option || "A"]; } catch { return [q.correct_option || "A"]; } })() : [q.correct_option || "A"],
      image_file: null,
      image_preview: q.question_image ? `${API_BASE.replace(/\/api$/, "")}/uploads/question-modules/${q.question_image}` : "",
    })) : [{ ...blankQ }]);
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h3 className="mb-1">New Question Module</h3>
            <form onSubmit={save} className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Entry Mode</label>
                <select className="form-select" value={entryMode} onChange={(e) => setEntryMode(e.target.value)}>
                  <option value="manual">Manual</option>
                  <option value="automatic">Automatic (From CSV Set)</option>
                </select>
              </div>
              {entryMode === "automatic" ? (
                <>
                  <div className="col-md-6">
                    <label className="form-label">CSV Set</label>
                    <select className="form-select" value={selectedCsvSetId} onChange={(e) => setSelectedCsvSetId(e.target.value)}>
                      <option value="">Select CSV Set</option>
                      {csvSets.map((s) => <option key={s.id} value={s.id}>{s.set_name} | {s.academy_category} | {s.course_name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    <button type="button" className="btn btn-outline-primary w-100" onClick={loadFromCsvSet}>Load Set</button>
                  </div>
                </>
              ) : null}
              <div className="col-md-4"><label className="form-label">Set Name</label><input className="form-control" value={form.set_name} onChange={(e) => setField("set_name", e.target.value)} required /></div>
              <div className="col-md-4"><label className="form-label">Academy Category</label><select className="form-select" value={form.academy_category} onChange={(e) => setForm((p) => ({ ...p, academy_category: e.target.value, course_model_id: "", course_category_id: "", course_id: "" }))} required><option value="">Select</option>{(meta.academy_categories || []).map((a) => <option key={a} value={a}>{a}</option>)}</select></div>
              <div className="col-md-4"><label className="form-label">Course Module</label><select className="form-select" value={form.course_model_id} onChange={(e) => setForm((p) => ({ ...p, course_model_id: e.target.value, course_category_id: "", course_id: "" }))} required><option value="">Select</option>{models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
              <div className="col-md-4"><label className="form-label">Course Category</label><select className="form-select" value={form.course_category_id} onChange={(e) => setForm((p) => ({ ...p, course_category_id: e.target.value, course_id: "" }))} required><option value="">Select</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
              <div className="col-md-4"><label className="form-label">Course Name</label><select className="form-select" value={form.course_id} onChange={(e) => setField("course_id", e.target.value)} required><option value="">Select</option>{courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</select></div>

              <div className="col-12"><hr /></div>
              <div className="col-12 d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Questions</h5>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setQuestions((p) => [...p, { ...blankQ }])}>Add Question</button>
              </div>
              {questions.map((q, i) => (
                <div className="col-12 border rounded p-3" key={i}>
                  <div className="row g-2">
                    <div className="col-md-4">
                      <label className="form-label">Question Type</label>
                      <select className="form-select" value={q.question_type} onChange={(e) => handleTypeChange(i, e.target.value)}>
                        {TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div className="col-md-8"><label className="form-label">Question</label><input className="form-control" value={q.question} onChange={(e) => setQuestion(i, "question", e.target.value)} required /></div>

                    {q.question_type.startsWith("image_") ? (
                      <div className="col-md-6">
                        <label className="form-label">Question Image</label>
                        <input type="file" accept=".jpg,.jpeg,image/jpeg" className="form-control" onChange={(e) => {
                          const f = e.target.files?.[0] || null;
                          setQuestion(i, "image_file", f);
                          setQuestion(i, "image_preview", f ? URL.createObjectURL(f) : "");
                        }} />
                        {q.image_preview ? <img src={q.image_preview} alt="preview" style={{ marginTop: 8, maxHeight: 120 }} /> : null}
                      </div>
                    ) : null}

                    {(q.question_type === "true_false" || q.question_type === "image_true_false") ? (
                      <div className="col-md-6">
                        <label className="form-label">Correct Answer</label>
                        <select className="form-select" value={q.correct_answers[0] || "A"} onChange={(e) => setQuestion(i, "correct_answers", [e.target.value])}>
                          <option value="A">True</option>
                          <option value="B">False</option>
                        </select>
                      </div>
                    ) : (
                      <>
                        {["A", "B", "C", "D"].map((key, idx2) => (
                          <div className="col-md-6" key={key}>
                            <label className="form-label">Option {key}</label>
                            <input className="form-control" value={q.options[idx2] || ""} onChange={(e) => setOption(i, idx2, e.target.value)} required />
                          </div>
                        ))}
                        <div className="col-md-8">
                          <label className="form-label">Correct Answer</label>
                          {(q.question_type === "multiple_choice" || q.question_type === "image_multiple_choice") ? (
                            <div className="d-flex gap-3 flex-wrap">
                              {["A", "B", "C", "D"].map((k) => (
                                <label key={k} className="form-check-label">
                                  <input className="form-check-input me-1" type="checkbox" checked={q.correct_answers.includes(k)} onChange={() => toggleMultiCorrect(i, k)} />
                                  {k}
                                </label>
                              ))}
                            </div>
                          ) : (
                            <select className="form-select" value={q.correct_answers[0] || "A"} onChange={(e) => setQuestion(i, "correct_answers", [e.target.value])}>
                              <option value="A">A</option>
                              <option value="B">B</option>
                              <option value="C">C</option>
                              <option value="D">D</option>
                            </select>
                          )}
                        </div>
                      </>
                    )}

                    <div className="col-md-3">
                      <button type="button" className="btn btn-outline-danger" onClick={() => setQuestions((p) => p.length > 1 ? p.filter((_, idx) => idx !== i) : p)}>Remove</button>
                    </div>
                  </div>
                </div>
              ))}
              <div className="col-md-3"><button type="submit" className="btn btn-primary w-100" disabled={saving}>{saving ? "Saving..." : "Save Question Module"}</button></div>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default NewQuestionModulePage;
